import React from 'react';

/**
 * Component-level error boundary
 * Provides graceful degradation for individual components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @param {string} [props.fallbackMessage] - Custom error message to display
 * @param {React.ReactNode} [props.fallback] - Custom fallback component
 * @param {boolean} [props.showRetry] - Whether to show retry button
 * @param {string} [props.componentName] - Name of component for logging
 */
class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error(`Component Error Boundary (${this.props.componentName || 'Unknown'}) caught an error:`, error, errorInfo);
    
    this.setState({ error });

    // In production, you might want to log this to an error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback component
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="p-4 bg-red-900/10 border border-red-600/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            
            <div className="flex-1">
              <p className="text-red-400 text-sm">
                {this.props.fallbackMessage || "This section is temporarily unavailable."}
              </p>
              
              {this.props.showRetry && (
                <button
                  onClick={this.handleRetry}
                  className="mt-2 text-xs text-red-300 hover:text-red-200 underline transition-colors"
                >
                  Try again
                </button>
              )}

              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-red-300">
                    Error Details (Dev)
                  </summary>
                  <pre className="mt-1 text-xs text-red-200 bg-red-900/20 p-2 rounded overflow-auto">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ComponentErrorBoundary;
